import {
  CHECKPOINT_CERO,
  coleccionDe,
  esTipoMutacion,
  faltanColecciones,
  MAX_MUTACIONES_POR_LOTE,
  parseHlc,
  receiveHlc,
  serializarColecciones,
  serializeCheckpoint,
  sealedSeasonId,
  serializeHlc,
  type Checkpoint,
  type Coleccion,
} from '@selflevel/domain';
import type {
  BootstrapResponse,
  Documento,
  PullResponse,
  PushResponse,
} from '@selflevel/contracts';
import { liveQuery } from 'dexie';
import { db, type OutboxItem } from './db';
import { ErrorDeApi, pedir, SinRed } from './api';
import { adoptarUsuario, getDeviceId, getUserId, guardarHlcRemoto } from './session';

/**
 * EL MOTOR DE SINCRONIZACIÓN, LADO CLIENTE.
 *
 * Cuatro reglas de operación, y las cuatro están en el PRD 6.4 porque las cuatro
 * se aprendieron rompiéndolas:
 *
 *  1. **Drena una sola pestaña.** Con Web Locks. Sin esto, dos pestañas empujan el
 *     mismo lote a la vez y el checkpoint que guarda cada una es distinto.
 *  2. **Backoff exponencial con jitter**, respetando `Retry-After`. El jitter no es
 *     un adorno: sin él, mil clientes que pierden la red a la vez vuelven a la vez.
 *  3. **A los 10 intentos, la mutación muere** y se avisa al usuario con opción de
 *     exportarla. Una cola que reintenta para siempre contra un error permanente
 *     bloquea todo lo que venga detrás.
 *  4. **Nunca se guarda en local algo que no esté ya empujado o en la outbox.** Es
 *     la regla que hace recuperable cualquier pérdida de IndexedDB (PRD 15.4), y la
 *     que permite que `wipeLocal()` sea una operación segura.
 */

const CLAVE_CHECKPOINT = 'checkpoint';
const CLAVE_CUENTA = 'syncAccountId';
const CLAVE_BOOTSTRAP = 'bootstrapHecho';
/** La última cuenta que tuvo este dispositivo. Sobrevive a cerrar sesión. */
const CLAVE_ULTIMA_CUENTA = 'ultimaCuenta';

const MAX_INTENTOS = 10;
const ESPERA_BASE_MS = 1_000;
const ESPERA_MAXIMA_MS = 60_000;

/** Mapa de colección del cable a tabla del almacén local. */
const TABLAS: Record<Coleccion, string> = {
  life_area: 'lifeAreas',
  identity_statement: 'identities',
  goal: 'goals',
  goal_milestone: 'goalMilestones',
  habit: 'habits',
  habit_entry: 'habitEntries',
  focus_session: 'focusSessions',
  journal_entry: 'journalEntries',
  mood_log: 'moodLogs',
  review_session: 'reviews',
  review_action: 'reviewActions',
  task: 'tasks',
  day_plan: 'dayPlans',
  week_plan: 'weekPlans',
  life_wheel: 'lifeWheels',
  dream: 'dreams',
  dream_step: 'dreamSteps',
  progression_ledger: 'ledger',
  achievement_unlock: 'milestones',
  sealed_season: 'seasons',
  external_event: 'externalEvents',
};

/**
 * Lo que este cliente sabe guardar, declarado en cada pull y bootstrap: el servidor
 * no le manda nada más. Se guarda en `meta` al terminar el bootstrap; cuando una
 * versión nueva declara alguna más, el servidor se las estuvo filtrando y hace
 * falta una descarga completa (`asegurarColecciones`).
 */
const CLAVE_COLECCIONES = 'coleccionesDeclaradas';
const COLECCIONES_DEL_CLIENTE = Object.keys(TABLAS) as Coleccion[];
const PARAM_COLECCIONES = `colecciones=${encodeURIComponent(serializarColecciones(COLECCIONES_DEL_CLIENTE))}`;

/**
 * Dos colecciones llegan con una forma distinta de la que usa el almacén local, y
 * se traducen aquí.
 *
 * No es un capricho: el almacén local las tiene indexadas por su clave natural
 * —`key`— porque la interfaz las busca así («¿tiene el usuario el hito
 * *constancia-30*?»), mientras que el servidor las indexa por `id` como todo lo
 * demás. Traducir dos de quince es más barato que cambiar la forma de ninguno de
 * los dos lados por simetría.
 */
function aFilaLocal(coleccion: Coleccion, row: Record<string, unknown>): Record<string, unknown> {
  if (coleccion === 'achievement_unlock') {
    const { achievementKey, ...resto } = row;
    return { ...resto, key: achievementKey };
  }
  if (coleccion === 'sealed_season') {
    const { seasonKey, fromDate, toDate, ...resto } = row;
    return { ...resto, key: seasonKey, from: fromDate, to: toDate };
  }
  return row;
}

// ===========================================================================
// ESTADO OBSERVABLE
// ===========================================================================

export type EstadoSync = {
  /** `apagado` cuando no hay cuenta: la app entera funciona igual. */
  readonly estado: 'apagado' | 'inactivo' | 'trabajando' | 'sin-red' | 'error';
  readonly pendientes: number;
  readonly muertas: number;
  readonly ultimaVez: string | null;
  readonly ultimoError: string | null;
  /** Si esta pestaña es la que drena. Las demás solo miran. */
  readonly lider: boolean;
};

let estado: EstadoSync = {
  estado: 'apagado',
  pendientes: 0,
  muertas: 0,
  ultimaVez: null,
  ultimoError: null,
  lider: false,
};

const oyentes = new Set<(e: EstadoSync) => void>();

export function observarSync(oyente: (e: EstadoSync) => void): () => void {
  oyentes.add(oyente);
  oyente(estado);
  return () => oyentes.delete(oyente);
}

export function estadoSync(): EstadoSync {
  return estado;
}

function actualizar(parcial: Partial<EstadoSync>): void {
  estado = { ...estado, ...parcial };
  for (const oyente of oyentes) oyente(estado);
}

// ===========================================================================
// ENCENDER Y APAGAR
// ===========================================================================

export async function cuentaVinculada(): Promise<string | null> {
  const fila = await db.meta.get(CLAVE_CUENTA);
  return typeof fila?.value === 'string' && fila.value.length > 0 ? fila.value : null;
}

/**
 * Vincula la cuenta y arranca. **Lo que ya existía en local no se pierde ni se
 * reenvía como cola**: el primer envío es un barrido de las tablas, no una
 * reproducción de mutaciones que nunca se encolaron (ver `mutations.ts`).
 */
export async function vincularCuenta(accountId: string): Promise<void> {
  const actual = await cuentaVinculada();
  // Ya estaba vinculada a esta cuenta: lo local ya subió, y volver a barrerlo
  // solo serviría para pisar cambios más nuevos de otros dispositivos.
  if (actual === accountId) {
    arrancar();
    return;
  }

  const anterior = actual ?? (await ultimaCuenta());
  if (anterior !== null && anterior !== accountId) {
    // Otra cuenta: su checkpoint, su arranque y su cola no son de esta.
    await db.transaction('rw', db.meta, db.outbox, async () => {
      await db.meta.delete(CLAVE_CHECKPOINT);
      await db.meta.delete(CLAVE_BOOTSTRAP);
      await db.outbox.clear();
    });
  }

  await db.meta.put({ key: CLAVE_CUENTA, value: accountId });
  await db.meta.put({ key: CLAVE_ULTIMA_CUENTA, value: accountId });
  await adoptarUsuario(accountId);
  await encolarTodoLoLocal();
  arrancar();
}

async function ultimaCuenta(): Promise<string | null> {
  const fila = await db.meta.get(CLAVE_ULTIMA_CUENTA);
  return typeof fila?.value === 'string' && fila.value.length > 0 ? fila.value : null;
}

/**
 * Si este dispositivo guarda lo de OTRA cuenta, vincular esta subiría esos datos
 * a quien no es su dueño. Se impide: primero hay que borrar este dispositivo.
 */
export async function tieneDatosDeOtraCuenta(accountId: string): Promise<boolean> {
  const anterior = (await cuentaVinculada()) ?? (await ultimaCuenta());
  if (anterior === null || anterior === accountId) return false;
  const cuentas = await Promise.all([
    db.lifeAreas.count(),
    db.habits.count(),
    db.habitEntries.count(),
    db.journalEntries.count(),
    db.focusSessions.count(),
    db.moodLogs.count(),
    db.tasks.count(),
    db.dayPlans.count(),
    db.weekPlans.count(),
    db.lifeWheels.count(),
    db.dreams.count(),
    db.dreamSteps.count(),
  ]);
  return cuentas.some((n) => n > 0);
}

/**
 * Desvincula. **No borra nada local**: el usuario sigue teniendo su app entera en
 * el dispositivo. Cerrar sesión no es borrar los datos, y confundir las dos cosas
 * es la forma más rápida de que alguien pierda dos años de registro por haber
 * tocado el botón equivocado.
 */
export async function desvincularCuenta(): Promise<void> {
  detener();
  await db.meta.delete(CLAVE_CUENTA);
  await db.meta.delete(CLAVE_CHECKPOINT);
  await db.meta.delete(CLAVE_BOOTSTRAP);
  await db.outbox.clear();
  actualizar({ estado: 'apagado', pendientes: 0, lider: false });
}

/**
 * ───────────────────────────────────────────────────────────────────────────
 * EL CICLO DE VIDA DEL MOTOR, y todo lo que tiene que cerrarse al pararlo.
 *
 * Cada cosa que el motor abre tiene aquí su variable y `detener()` la cierra:
 * la petición en vuelo, el SSE y su reconexión, el temporizador de reintento,
 * el despertador de la cola, la vigilancia de la cola, la espera del evento
 * `online` y el cerrojo entre pestañas. Si algo se
 * abre y no está en esta lista, es una fuga.
 *
 * LA GENERACIÓN. Cada `arrancar()` abre una generación nueva, y todo lo que
 * corre de forma asíncrona comprueba que sigue siendo la suya antes de tocar
 * nada. Sin ella, una ejecución vieja que despierta tarde —la que estaba
 * esperando el cerrojo, la que tenía un `fetch` en vuelo— podía hacer cosas en
 * nombre de un motor que ya no existe. El caso que lo hacía visible: cerrar
 * sesión y volver a entrar en menos de un segundo dejaba el cerrojo retenido
 * por la ejecución anterior y la nueva esperándolo para siempre.
 * ───────────────────────────────────────────────────────────────────────────
 */
let corriendo = false;
let generacion = 0;
let abortar: AbortController | null = null;
let fuente: EventSource | null = null;
let temporizador: ReturnType<typeof setTimeout> | null = null;
let despertador: ReturnType<typeof setTimeout> | null = null;
let empujon: ReturnType<typeof setTimeout> | null = null;
let reconexion: ReturnType<typeof setTimeout> | null = null;
let vigilancia: { unsubscribe(): void } | null = null;
let soltarCerrojo: (() => void) | null = null;
let alVolverLaRed: (() => void) | null = null;
/** Fallos seguidos del ciclo y del SSE. Hacen que las esperas crezcan. */
let fallosSeguidos = 0;
let fallosDelStream = 0;

export function arrancar(): void {
  if (corriendo) return;
  corriendo = true;
  generacion += 1;
  fallosSeguidos = 0;
  fallosDelStream = 0;
  abortar = new AbortController();
  void pedirLiderazgo(generacion, abortar.signal);
}

export function detener(): void {
  corriendo = false;
  abortar?.abort();
  abortar = null;
  fuente?.close();
  fuente = null;
  for (const t of [temporizador, despertador, empujon, reconexion]) if (t) clearTimeout(t);
  temporizador = null;
  despertador = null;
  empujon = null;
  reconexion = null;
  vigilancia?.unsubscribe();
  vigilancia = null;
  if (alVolverLaRed) window.removeEventListener('online', alVolverLaRed);
  alVolverLaRed = null;
  // El cerrojo se suelta AQUÍ, en el acto, y no cuando un sondeo se dé cuenta.
  soltarCerrojo?.();
  soltarCerrojo = null;
  // Un motor parado está APAGADO, no en el último estado que tuviera. Antes se
  // quedaba pintando «error» o «sin red» de un intento que ya no existe.
  actualizar({ estado: 'apagado', lider: false });
}

/** Solo cuando el navegador lo sabe seguro. `true` no garantiza red de verdad
 *  —un portal cautivo dice que sí—, pero `false` sí garantiza que no la hay. */
function sinRed(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * SIN RED, SE ESPERA A QUE VUELVA. Un solo oyente de `online` para todo el
 * motor —el ciclo, el SSE y la vigilancia de la cola lo comparten—, y es de un
 * solo uso: cuando la red vuelve, se reabre el SSE y se sincroniza, con los
 * contadores de fallo a cero porque los fallos eran de la red, no del servidor.
 */
function esperarRed(): void {
  if (alVolverLaRed || !corriendo) return;
  const gen = generacion;
  alVolverLaRed = () => {
    alVolverLaRed = null;
    if (!vigente(gen)) return;
    fallosSeguidos = 0;
    fallosDelStream = 0;
    if (!fuente) abrirStream();
    void sincronizarAhora();
  };
  window.addEventListener('online', alVolverLaRed, { once: true });
}

/** Si esta ejecución sigue siendo la del motor en marcha. */
function vigente(gen: number): boolean {
  return corriendo && gen === generacion;
}

/**
 * Un aborto NO es un fallo de red. Lo provoca `detener()`, y `pedir` lo envuelve
 * en `SinRed` como cualquier `fetch` rechazado. Tratarlo como red caída hacía
 * tres cosas mal: pintaba «sin red» encima del «apagado» recién puesto,
 * programaba un reintento con el motor ya parado, y aparcaba las mutaciones
 * pendientes con una espera que el siguiente arranque tenía que cumplir.
 */
function esAborto(error: unknown): boolean {
  if (error instanceof SinRed) return esAborto(error.cause);
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * UN SOLO ESCRITOR ENTRE PESTAÑAS (PRD 6.4).
 *
 * `navigator.locks.request` con una promesa que no se resuelve hasta que la
 * pestaña muere: mientras la tenga, ninguna otra entra. Las demás esperan en la
 * cola del cerrojo y toman el relevo **solas** cuando esta se cierre, sin sondeo
 * ni coordinación.
 *
 * Si el navegador no tiene Web Locks (Safari viejo), se sigue adelante sin
 * exclusión: es mejor sincronizar con riesgo de un push duplicado —que el servidor
 * ya trata como idempotente— que no sincronizar. Pero se nota en el estado.
 */
async function pedirLiderazgo(gen: number, signal: AbortSignal): Promise<void> {
  const locks = navigator.locks;
  if (!locks) {
    actualizar({ lider: true });
    await bucle(gen);
    return;
  }

  try {
    // Con `signal`, parar el motor saca a esta pestaña de la COLA del cerrojo.
    // Sin él, una pestaña que se paró mientras esperaba turno lo recibía más
    // tarde y se ponía a sincronizar en nombre de un motor apagado.
    await locks.request('selflevel-sync', { mode: 'exclusive', signal }, async () => {
      if (!vigente(gen)) return;
      actualizar({ lider: true });
      try {
        await bucle(gen);
      } finally {
        if (gen === generacion) actualizar({ lider: false });
      }
    });
  } catch (error) {
    // El aborto de la cola es la forma normal de parar mientras se espera.
    if (!esAborto(error)) throw error;
  }
}

/**
 * El bucle. No es un `setInterval`: es una promesa que vive mientras la pestaña
 * sea líder, y que se despierta por tres cosas —un cambio local, un aviso del SSE,
 * o el reintento programado—. Un intervalo fijo haría una petición cada N segundos
 * aunque no hubiera nada, que en un móvil es batería tirada.
 */
async function bucle(gen: number): Promise<void> {
  const cuenta = await cuentaVinculada();
  if (!cuenta || !vigente(gen)) {
    actualizar({ estado: 'apagado' });
    return;
  }

  /**
   * EL ARRANQUE VA DENTRO DEL CICLO, no delante. Antes se hacía aquí, sin nada
   * que recogiera su fallo: abrir la app sin red o con el servidor caído hacía
   * que `bootstrap()` lanzara, la excepción subía por el cerrojo y acababa como
   * una promesa rechazada que no recogía nadie. **El motor moría en silencio**
   * —sin reintento, sin SSE, sin cerrojo— hasta recargar la pestaña. Lo cazó la
   * sonda del ciclo de vida: una petición en veintiséis segundos y luego nada.
   * Dentro de `sincronizarAhora` el fallo lo recoge el mismo `catch` que ya
   * sabe reintentar con espera creciente.
   */
  abrirStream();
  vigilarCola(gen);
  await sincronizarAhora();

  /**
   * EL CERROJO SE SOSTIENE SIN SONDEO. Antes era un `setTimeout` de un segundo
   * que se reprogramaba mientras el motor estuviera en marcha: un despertar por
   * segundo durante toda la vida de la pestaña, para nada. Ahora la promesa
   * queda abierta y `detener()` la resuelve en el acto.
   */
  await new Promise<void>((resolver) => {
    if (!vigente(gen)) {
      resolver();
      return;
    }
    soltarCerrojo = resolver;
  });
}

/**
 * LO QUE ESCRIBES DESPIERTA AL MOTOR.
 *
 * El comentario del bucle lo decía desde el principio —«se despierta por un
 * cambio local»— y no lo hacía nadie: ninguna mutación avisaba. Con un solo
 * dispositivo, lo que marcabas se quedaba en la cola hasta abrir la app la vez
 * siguiente, porque el SSE solo avisa de cambios de OTROS dispositivos.
 *
 * Se vigila la COLA y no las mutaciones, y lo hace el líder. Así cubre lo que se
 * escribe en cualquier pestaña —Dexie propaga los cambios entre pestañas— y
 * solo drena una, que es la regla del PRD 6.4. Con una espera corta, para que
 * marcar cinco casillas seguidas sea un envío y no cinco.
 */
function vigilarCola(gen: number): void {
  vigilancia?.unsubscribe();
  vigilancia = liveQuery(() => db.outbox.where('state').equals('pending').count()).subscribe({
    next: (pendientes) => {
      if (!vigente(gen) || pendientes === 0) return;
      if (empujon) clearTimeout(empujon);
      empujon = setTimeout(() => {
        empujon = null;
        if (vigente(gen)) void sincronizarAhora();
      }, 400);
    },
    error: (error: unknown) => console.error('[sync] vigilancia de la cola', error),
  });
}

// ===========================================================================
// BOOTSTRAP
// ===========================================================================

async function leerCheckpoint(): Promise<Checkpoint> {
  const fila = await db.meta.get(CLAVE_CHECKPOINT);
  const valor = fila?.value as Checkpoint | undefined;
  return valor?.updatedAt ? valor : CHECKPOINT_CERO;
}

async function guardarCheckpoint(checkpoint: Checkpoint): Promise<void> {
  await db.meta.put({ key: CLAVE_CHECKPOINT, value: checkpoint });
}

async function asegurarBootstrap(): Promise<void> {
  const hecho = await db.meta.get(CLAVE_BOOTSTRAP);
  if (hecho?.value === true) return;
  await bootstrap();
}

/**
 * Descarga inicial, paginada. Se marca como hecha **al final**: si se cortara a
 * mitad, el siguiente arranque la repetiría desde su checkpoint en vez de dar por
 * bueno un almacén incompleto.
 */
export async function bootstrap(): Promise<void> {
  const deviceId = getDeviceId();
  let since: string | undefined;

  for (;;) {
    const respuesta = await pedir<BootstrapResponse>(
      `/sync/bootstrap?limit=500&${PARAM_COLECCIONES}${since ? `&since=${encodeURIComponent(since)}` : ''}`,
      { deviceId, ...(abortar ? { signal: abortar.signal } : {}) },
    );

    await aplicarDocumentos(respuesta.documents);
    await guardarCheckpoint(respuesta.checkpoint);

    if (respuesta.settings) await aplicarAjustesDelServidor(respuesta.settings);
    if (!respuesta.hasMore) break;
    const siguiente = serializeCheckpoint(respuesta.checkpoint);
    sinAvanceEsFallo(since, siguiente);
    since = siguiente;
  }

  await db.meta.put({ key: CLAVE_COLECCIONES, value: COLECCIONES_DEL_CLIENTE });
  await db.meta.put({ key: CLAVE_BOOTSTRAP, value: true });
}

/**
 * Tras actualizar la app, si declara colecciones que la versión anterior no
 * declaraba, el servidor se las filtró y su checkpoint ya pasó por encima: el
 * `pull` no las traerá nunca. Se vuelve a descargar todo por el camino del
 * re-bootstrap, que no toca la outbox. Va después del push, para que lo pendiente
 * esté ya en el servidor cuando se vacían las tablas.
 */
async function asegurarColecciones(): Promise<void> {
  const fila = await db.meta.get(CLAVE_COLECCIONES);
  const guardadas = Array.isArray(fila?.value) ? (fila.value as string[]) : null;
  if (faltanColecciones(guardadas, COLECCIONES_DEL_CLIENTE)) await reBootstrap();
}

/**
 * RE-BOOTSTRAP (PRD 6.10 · escenario S8). Pasa cuando el checkpoint es anterior a
 * la purga de lápidas: el servidor ya no puede decirle qué se borró mientras
 * estuvo fuera, y servirle desde ahí resucitaría filas muertas.
 *
 * Se vacían **solo las tablas replicadas**. La outbox NO se toca: lo que el
 * usuario escribió y aún no ha salido es lo único que el servidor no tiene, y
 * borrarlo sería perder datos para arreglar un checkpoint.
 */
export async function reBootstrap(): Promise<void> {
  await db.transaction('rw', [...TABLAS_LOCALES, 'meta'], async () => {
    // Hitos y temporadas selladas son conjuntos que solo crecen (PRD 8.4): uno
    // ganado sin red aún no está en el servidor, y vaciarlos lo borraría.
    for (const tabla of TABLAS_LOCALES) {
      if (tabla === 'milestones' || tabla === 'seasons') continue;
      await db.table(tabla).clear();
    }
    await db.meta.delete(CLAVE_CHECKPOINT);
    await db.meta.delete(CLAVE_BOOTSTRAP);
  });
  await bootstrap();
}

const TABLAS_LOCALES = [...new Set(Object.values(TABLAS))];

// ===========================================================================
// PULL
// ===========================================================================

async function aplicarDocumentos(documentos: readonly Documento[]): Promise<void> {
  if (documentos.length === 0) return;

  await db.transaction('rw', TABLAS_LOCALES, async () => {
    for (const documento of documentos) {
      /**
       * Una colección sin tabla local se salta en vez de abortar la transacción:
       * `db.table(undefined)` lanzaba, el checkpoint no avanzaba y el dispositivo
       * dejaba de recibir todo. El servidor ya filtra por lo que declaramos; esto
       * es la red de debajo.
       */
      const nombre = (TABLAS as Partial<Record<string, string>>)[documento.collection];
      if (!nombre) {
        console.warn('[sync] colección desconocida, se ignora', documento.collection);
        continue;
      }
      const tabla = db.table(nombre);

      if (documento.deleted) {
        /**
         * Una lápida borra la copia local de verdad. El servidor se queda con la
         * suya 90 días para poder contárselo a otros dispositivos; el cliente no
         * necesita guardar basura que el usuario ya quitó.
         */
        await tabla.delete(documento.id);
        continue;
      }
      if (!documento.row) continue;
      await tabla.put(aFilaLocal(documento.collection, documento.row));
    }
  });
}

export async function pull(): Promise<number> {
  const deviceId = getDeviceId();
  let total = 0;

  for (;;) {
    const desde = await leerCheckpoint();
    let respuesta: PullResponse;
    try {
      respuesta = await pedir<PullResponse>(
        `/sync/pull?since=${encodeURIComponent(serializeCheckpoint(desde))}&limit=500&${PARAM_COLECCIONES}`,
        { deviceId, ...(abortar ? { signal: abortar.signal } : {}) },
      );
    } catch (error) {
      if (error instanceof ErrorDeApi && error.code === 'checkpoint-too-old') {
        await reBootstrap();
        return total;
      }
      throw error;
    }

    await aplicarDocumentos(respuesta.documents);
    await guardarCheckpoint(respuesta.checkpoint);
    total += respuesta.documents.length;
    if (!respuesta.hasMore) return total;
    sinAvanceEsFallo(serializeCheckpoint(desde), serializeCheckpoint(respuesta.checkpoint));
  }
}

/**
 * LAS DOS PAGINACIONES —el arranque y el `pull`— son un `for (;;)` que confía en
 * una promesa del servidor: si dice «hay más», el checkpoint que devuelve está
 * más adelante que el anterior. Si alguna vez no lo está —un fallo del
 * servidor, dos filas con el mismo instante y el mismo id después de una
 * restauración— el cliente pedía la misma página para siempre, a toda
 * velocidad y sin espera entre vueltas. Un bucle que no puede avanzar se para y
 * lo dice; el reintento del ciclo, que sí espera y crece, se encarga de lo demás.
 */
function sinAvanceEsFallo(antes: string | undefined, despues: string): void {
  if (antes !== undefined && antes === despues) {
    throw new Error('el servidor dijo que había más, pero el checkpoint no avanzó');
  }
}

// ===========================================================================
// PUSH
// ===========================================================================

/**
 * Empuja un lote. Devuelve si queda algo por empujar, para que el bucle siga sin
 * esperar al siguiente despertar.
 */
async function push(): Promise<boolean> {
  const deviceId = getDeviceId();
  const ahora = Date.now();

  /**
   * Solo lo que ya puede salir. `proximoIntentoMs` implementa el backoff: una
   * mutación que acaba de fallar no se reenvía en el mismo ciclo, pero tampoco
   * bloquea a las que van detrás si son de otra fila.
   */
  const pendientes = porTamano(
    (await db.outbox.where('state').equals('pending').sortBy('seq'))
      .filter((item) => (item.nextAttemptMs ?? 0) <= ahora)
      .slice(0, MAX_MUTACIONES_POR_LOTE),
  );

  if (pendientes.length === 0) return false;

  let respuesta: PushResponse;
  try {
    respuesta = await pedir<PushResponse>('/sync/push', {
      deviceId,
      metodo: 'POST',
      cuerpo: {
        deviceId,
        mutations: pendientes.map((item) => ({
          mutationId: item.id,
          seq: item.seq,
          hlc: item.hlc,
          type: item.type,
          fields: item.fields ?? [],
          row: item.row,
        })),
      },
      ...(abortar ? { signal: abortar.signal } : {}),
    });
  } catch (error) {
    // Un aborto lo provoca `detener()`: no es un fallo de la mutación y no debe
    // dejarla aparcada con una espera que el siguiente arranque tenga que cumplir.
    if (!esAborto(error)) await registrarFallo(pendientes, error);
    throw error;
  }

  await db.transaction('rw', ['outbox', 'meta', ...TABLAS_LOCALES], async () => {
    for (const resultado of respuesta.results) {
      const item = pendientes.find((p) => p.id === resultado.mutationId);
      if (!item) continue;

      /**
       * **Se adopta la fila que devuelve el servidor.** Con LWW por campo la
       * fila guardada es la fusión de varias escrituras, no la que mandó este
       * dispositivo, y quedarse con la copia optimista deja una divergencia que
       * el `pull` no corrige nunca: el campo que este cliente no ganó no movió
       * `updated_at`, así que la corrección nunca queda por delante del
       * checkpoint. Lo encontró el escenario S12 del motor.
       */
      if (resultado.row && esTipoMutacion(item.type)) {
        const coleccion = coleccionDe(item.type);
        await db.table(TABLAS[coleccion]).put(aFilaLocal(coleccion, resultado.row));
      }

      if (resultado.status === 'rejected') {
        // No se aplicará nunca. Se retira de circulación con su cuerpo, para que
        // el usuario pueda exportarla y preguntar qué pasó.
        await db.outbox.update(item.id, {
          state: 'dead',
          lastError: `${resultado.code ?? 'rechazada'}: ${resultado.detail ?? ''}`,
        });
        continue;
      }
      // `applied`, `stale` y `duplicate`: el servidor no la va a aceptar mejor
      // más tarde, así que sale de la cola.
      await db.outbox.delete(item.id);
    }
    /**
     * El checkpoint NO se toma de aquí. El del push es la fila más nueva del
     * usuario venga de donde venga, y guardarlo saltaba lo que otro dispositivo
     * escribió entre el último pull y este push: no bajaba nunca. Lo mueve solo
     * el pull, que siempre va justo después.
     */
  });

  const quedan = await db.outbox.where('state').equals('pending').count();
  return quedan > 0;
}

/**
 * El servidor acepta cuerpos de hasta 1 MB. Un barrido con muchas entradas de
 * diario lo superaba, el 413 retiraba el lote entero y reintentarlo armaba el
 * mismo lote. Se corta por bytes, dejando margen; siempre sale al menos una.
 */
const MAX_BYTES_POR_LOTE = 700_000;

function porTamano(items: readonly OutboxItem[]): OutboxItem[] {
  const lote: OutboxItem[] = [];
  let bytes = 0;
  for (const item of items) {
    const tamano = JSON.stringify(item.row).length + 200;
    if (lote.length > 0 && bytes + tamano > MAX_BYTES_POR_LOTE) break;
    lote.push(item);
    bytes += tamano;
  }
  return lote;
}

/**
 * Anota el fallo y programa el reintento.
 *
 * El jitter de ±20 % no es cosmético: sin él, todos los clientes que pierden la
 * red en el mismo momento —una caída del servidor, por ejemplo— vuelven a la vez
 * y tumban el servidor justo cuando acaba de levantarse.
 */
async function registrarFallo(items: readonly OutboxItem[], error: unknown): Promise<void> {
  const esDeRed = error instanceof SinRed;
  const deApi = error instanceof ErrorDeApi ? error : null;
  const mensaje = error instanceof Error ? error.message : String(error);

  await db.transaction('rw', db.outbox, async () => {
    for (const item of items) {
      const intentos = item.attempts + 1;

      /**
       * Un fallo de red NO cuenta intentos. Un usuario con el móvil en un pueblo
       * sin cobertura acumularía diez intentos en un minuto y perdería lo que
       * escribió sin haber hecho nada mal. Solo los rechazos del servidor agotan.
       */
      if (esDeRed) {
        await db.outbox.update(item.id, {
          nextAttemptMs: Date.now() + espera(Math.min(item.attempts, 5)),
          lastError: mensaje,
        });
        continue;
      }

      /**
       * Sesión caducada (401) o app desactualizada (426): no es culpa de la
       * mutación. Retirarla mandaba hasta 500 cambios a la papelera por un login
       * vencido. Se aparcan sin gastar intentos hasta que se vuelva a entrar o
       * se actualice.
       */
      if (deApi && (deApi.status === 401 || deApi.status === 426)) {
        await db.outbox.update(item.id, {
          nextAttemptMs: Date.now() + 5 * 60_000,
          lastError: mensaje,
        });
        continue;
      }

      if (deApi && !deApi.retryable) {
        await db.outbox.update(item.id, { state: 'dead', attempts: intentos, lastError: mensaje });
        continue;
      }

      if (intentos >= MAX_INTENTOS) {
        await db.outbox.update(item.id, { state: 'dead', attempts: intentos, lastError: mensaje });
        continue;
      }

      await db.outbox.update(item.id, {
        attempts: intentos,
        // El servidor manda: si pidió esperar, se espera lo que pidió.
        nextAttemptMs:
          Date.now() + (deApi?.retryAfterS ? deApi.retryAfterS * 1000 : espera(intentos)),
        lastError: mensaje,
      });
    }
  });
}

function espera(intentos: number): number {
  const base = Math.min(ESPERA_BASE_MS * 2 ** intentos, ESPERA_MAXIMA_MS);
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(500, Math.round(base + jitter));
}

// ===========================================================================
// EL CICLO
// ===========================================================================

/**
 * Devuelve a la cola lo que se retiró.
 *
 * Existe porque la causa más común de una mutación retirada **no es un dato malo del
 * usuario: es un fallo nuestro que ya se arregló** —un campo renombrado, una validación
 * mal puesta, una versión de cliente y otra de servidor—. Sin esto, el arreglo llega y
 * el dato sigue retirado para siempre.
 *
 * El riesgo de reintentar algo que de verdad es inválido es cero: el servidor lo
 * rechaza otra vez y vuelve a salir de la cola.
 */
export async function reintentarRetiradas(): Promise<number> {
  const retiradas = await db.outbox.where('state').equals('dead').toArray();
  await db.transaction('rw', db.outbox, async () => {
    for (const item of retiradas) {
      await db.outbox.update(item.id, {
        state: 'pending',
        attempts: 0,
        nextAttemptMs: 0,
        lastError: null,
      });
    }
  });
  await sincronizarAhora();
  return retiradas.length;
}

/** Pidieron sincronizar mientras ya se sincronizaba: se repite al terminar. */
let repetir = false;

export async function sincronizarAhora(): Promise<void> {
  if (!(await cuentaVinculada())) return;
  if (estado.estado === 'trabajando') {
    // Un aviso del servidor puede llegar cuando el pull de este ciclo ya se
    // respondió: ignorarlo dejaba ese cambio sin bajar hasta el siguiente aviso.
    repetir = true;
    return;
  }
  repetir = false;
  // Sin red, intentar es fallar al instante. Cubre de una vez todo lo que llama
  // aquí: los temporizadores que ya estaban puestos cuando se fue la red, el
  // despertador de la cola y el botón de Cuenta.
  if (sinRed()) {
    actualizar({ estado: 'sin-red' });
    esperarRed();
    return;
  }

  actualizar({ estado: 'trabajando', ultimoError: null });
  try {
    // El arranque, si falta. Va aquí para que su fallo lo recoja el `catch` de
    // abajo y se reintente como cualquier otro: ver el comentario de `bucle`.
    await asegurarBootstrap();

    // Push antes que pull, siempre. Lo que este dispositivo escribió tiene que
    // llegar al servidor antes de que el servidor le diga lo que hay, o la
    // respuesta del pull pisaría la pantalla con un estado anterior al suyo.
    let quedan = true;
    let vueltas = 0;
    while (quedan && vueltas < 20) {
      quedan = await push();
      vueltas += 1;
    }
    await asegurarColecciones();
    await pull();
    await subirAjustesSiHaceFalta();

    fallosSeguidos = 0;
    actualizar({
      estado: 'inactivo',
      ultimaVez: new Date().toISOString(),
      pendientes: await db.outbox.where('state').equals('pending').count(),
      muertas: await db.outbox.where('state').equals('dead').count(),
    });
    await programarDespertador();
    if (repetir && corriendo) void sincronizarAhora();
  } catch (error) {
    // Parar el motor NO es un fallo: ver `esAborto`. Ni se pinta ni se reintenta.
    if (esAborto(error) || !corriendo) {
      // Por la función y no por `estado.estado`: el comprobador lo estrecha por
      // el `return` del principio y no sabe que `actualizar()` lo cambió después.
      if (estadoSync().estado === 'trabajando') {
        actualizar({ estado: corriendo ? 'inactivo' : 'apagado' });
      }
      return;
    }
    const sinRed = error instanceof SinRed;
    fallosSeguidos += 1;
    actualizar({
      estado: sinRed ? 'sin-red' : 'error',
      ultimoError: error instanceof Error ? error.message : String(error),
      pendientes: await db.outbox.where('state').equals('pending').count(),
      muertas: await db.outbox.where('state').equals('dead').count(),
    });
    programarReintento();
  }
}

/**
 * EL REINTENTO CRECE. Era siempre `espera(2)` —unos cuatro segundos—, así que un
 * servidor caído o un móvil sin cobertura durante una tarde entera recibían una
 * petición cada cuatro segundos mientras la pestaña estuviera abierta: batería
 * tirada y, si el servidor estaba cayéndose, más carga justo entonces. Ahora
 * crece con los fallos seguidos hasta el tope de un minuto, y vuelve a empezar
 * en cuanto un ciclo sale bien.
 *
 * Y SIN RED NO SE INSISTE. Si el navegador sabe que no hay conexión, no se
 * programa nada: se espera al evento `online`, que llega solo cuando vuelve.
 */
function programarReintento(): void {
  if (!corriendo) return;
  if (temporizador) clearTimeout(temporizador);
  temporizador = null;

  if (sinRed()) {
    esperarRed();
    return;
  }

  // El SSE NO se reabre desde aquí: tiene su propia reconexión. Con los dos
  // caminos abriéndolo, la sonda veía dos aperturas separadas por 0,1 s.
  temporizador = setTimeout(
    () => {
      temporizador = null;
      if (corriendo) void sincronizarAhora();
    },
    espera(Math.min(fallosSeguidos, 6)),
  );
}

/**
 * LA COLA VARADA. Una mutación que falla con un error reintentable se aparca con
 * su espera —hasta un minuto—, pero nadie programaba despertarse a esa hora: si
 * el resto del ciclo salía bien, el motor quedaba «inactivo» y esa mutación se
 * quedaba en la cola hasta que pasara otra cosa. Aquí se mira cuándo vence la
 * primera y se pone el despertador para entonces.
 */
async function programarDespertador(): Promise<void> {
  if (despertador) clearTimeout(despertador);
  despertador = null;
  if (!corriendo) return;

  const aparcadas = await db.outbox.where('state').equals('pending').toArray();
  const vence = aparcadas.reduce<number | null>((min, item) => {
    const cuando = item.nextAttemptMs ?? 0;
    return min === null || cuando < min ? cuando : min;
  }, null);
  if (vence === null || !corriendo) return;

  despertador = setTimeout(
    () => {
      despertador = null;
      if (corriendo) void sincronizarAhora();
    },
    Math.max(500, vence - Date.now()),
  );
}

/**
 * El SSE. Una sola conexión, y **el evento no trae la fila**: trae el aviso, y el
 * cliente hace `pull` desde su checkpoint. Es el camino que ya está probado; un
 * segundo camino de escritura al almacén local habría que mantenerlo igual de
 * correcto que el primero, con su LWW incluido.
 */
function abrirStream(): void {
  if (fuente || !corriendo) return;
  // El dispositivo va en la query porque `EventSource` no sabe mandar cabeceras
  // propias; sin él, la API respondía 400 y el stream no llegaba a abrirse.
  const url = `/api/sync/stream?device=${encodeURIComponent(getDeviceId())}`;
  const esta = new EventSource(url, { withCredentials: true });
  fuente = esta;

  esta.onopen = () => {
    fallosDelStream = 0;
  };

  esta.onmessage = (evento) => {
    try {
      const datos = JSON.parse(evento.data as string) as { event: string; kind?: string };
      if (datos.event === 'cambio') void sincronizarAhora();
      // Los avisos sociales no tocan la base local: se reenvían a la ventana para que
      // la pantalla que los muestra (el Círculo) se refresque si está abierta.
      if (datos.event === 'social') {
        window.dispatchEvent(new CustomEvent('selflevel:social', { detail: datos.kind ?? '' }));
      }
    } catch {
      // Un evento que no se entiende se ignora: la próxima sincronización lo
      // arreglará de todos modos. Aquí no hay nada que salvar.
    }
  };

  /**
   * `EventSource` reconecta solo mientras el fallo sea de red. Si el servidor
   * responde con un error —la sesión caducó, un 502 del proxy— lo cierra del
   * todo, y ANTES nadie lo volvía a abrir: esa pestaña se quedaba sin avisos de
   * los otros dispositivos hasta recargarla. Ahora se reabre, con una espera que
   * crece —una sesión caducada respondería 401 cada vez, y reabrir cada cuatro
   * segundos sería un bucle contra el servidor—, y vuelve a cero al conectar.
   */
  esta.onerror = () => {
    // SIN RED SE CIERRA A MANO. Mientras el fallo es de red, `EventSource` se
    // queda «conectando» y reintenta solo, cada pocos segundos, para siempre: la
    // sonda contó tres aperturas en quince segundos con el móvil sin cobertura.
    // Se cierra, y el oyente de `online` lo reabre cuando vuelva.
    if (sinRed()) {
      esta.close();
      if (fuente === esta) fuente = null;
      esperarRed();
      return;
    }
    if (esta.readyState !== EventSource.CLOSED) return;
    if (fuente === esta) fuente = null;
    if (!corriendo) return;
    fallosDelStream += 1;
    const gen = generacion;
    if (reconexion) clearTimeout(reconexion);
    reconexion = setTimeout(
      () => {
        reconexion = null;
        if (vigente(gen) && !fuente) abrirStream();
      },
      espera(Math.min(fallosDelStream, 6)),
    );
  };
}

// ===========================================================================
// VINCULAR UNA CUENTA A DATOS QUE YA EXISTÍAN
// ===========================================================================

/**
 * El primer envío de un usuario que llevaba meses usando la app sin cuenta.
 *
 * Barre las tablas y encola una mutación por fila. **No reproduce una cola
 * histórica**, porque nunca se encoló nada: sin cuenta no hay servidor, y
 * encolarlo igual habría hecho crecer IndexedDB con filas muertas (ver
 * `mutations.ts`). Las tablas son la fuente, y esto es leerlas.
 *
 * Los ids son los mismos que ya tenían, así que si el usuario ya había
 * sincronizado ese dato desde otro dispositivo, esto no duplica: converge.
 */
async function encolarTodoLoLocal(): Promise<void> {
  const { encolarFila } = await import('./mutations');

  const tareas: { tipo: string; filas: unknown[] }[] = [
    { tipo: 'area.upsert', filas: await db.lifeAreas.toArray() },
    { tipo: 'identity.upsert', filas: await db.identities.toArray() },
    { tipo: 'goal.upsert', filas: await db.goals.toArray() },
    { tipo: 'goalMilestone.upsert', filas: await db.goalMilestones.toArray() },
    { tipo: 'habit.upsert', filas: await db.habits.toArray() },
    { tipo: 'habit.setStatus', filas: await db.habitEntries.toArray() },
    { tipo: 'focus.record', filas: await db.focusSessions.toArray() },
    { tipo: 'journal.save', filas: await db.journalEntries.toArray() },
    { tipo: 'mood.record', filas: await db.moodLogs.toArray() },
    { tipo: 'review.save', filas: await db.reviews.toArray() },
    { tipo: 'reviewAction.save', filas: await db.reviewActions.toArray() },
    { tipo: 'task.upsert', filas: await db.tasks.toArray() },
    { tipo: 'dayPlan.save', filas: await db.dayPlans.toArray() },
    { tipo: 'weekPlan.save', filas: await db.weekPlans.toArray() },
    { tipo: 'wheel.save', filas: await db.lifeWheels.toArray() },
    { tipo: 'dream.upsert', filas: await db.dreams.toArray() },
    { tipo: 'dreamStep.upsert', filas: await db.dreamSteps.toArray() },
    // Las selladas sin cuenta no se encolaron: sin esto no llegarían nunca.
    {
      tipo: 'season.seal',
      filas: (await db.seasons.toArray()).map((t) => ({
        ...t,
        id: sealedSeasonId(getUserId(), t.key),
      })),
    },
  ];

  const deviceId = getDeviceId();
  for (const tarea of tareas) {
    for (const fila of tarea.filas) {
      const registro = fila as Record<string, unknown>;
      await encolarFila(tarea.tipo, registro, hlcDeLaFila(registro, deviceId));
    }
  }
}

const MARCAS_DE_FILA = [
  'ritualSealedAt',
  'sealedAt',
  'updatedAt',
  'notedAt',
  'endedAt',
  'completedAt',
  'doneAt',
  'adoptedAt',
  'createdAt',
];

/**
 * El HLC del barrido es el del momento en que se escribió la fila, no el de
 * ahora. Si el servidor no la tiene, entra igual; si tiene una versión más nueva
 * de otro dispositivo, gana esa (LWW). Con el HLC de ahora, volver a entrar
 * revertía las ediciones que otros dispositivos hicieron mientras tanto. Una
 * fila sin marca va con 0: solo entra si el servidor no la tiene.
 */
function hlcDeLaFila(fila: Record<string, unknown>, deviceId: string): string {
  for (const campo of MARCAS_DE_FILA) {
    const valor = fila[campo];
    if (typeof valor !== 'string') continue;
    const ms = Date.parse(valor);
    if (Number.isFinite(ms)) {
      return serializeHlc({ ms: Math.min(ms, Date.now()), counter: 0, deviceId });
    }
  }
  return serializeHlc({ ms: 0, counter: 0, deviceId });
}

/**
 * Sube los ajustes si el usuario los cambió. Se marca sucio en vez de encolar una
 * mutación porque no son una colección replicada: no hay fila que fusionar, solo
 * una preferencia que el último en cambiarla gana.
 */
async function subirAjustesSiHaceFalta(): Promise<void> {
  const sucio = await db.meta.get('ajustesSucios');
  if (!sucio?.value) return;

  const ajustes = await db.settings.get('unica');
  if (!ajustes) return;

  await pedir('/ajustes', {
    deviceId: getDeviceId(),
    metodo: 'POST',
    cuerpo: {
      dayStartOffsetMin: ajustes.dayStartOffsetMin,
      tz: ajustes.tz,
      locale: ajustes.locale,
      theme: ajustes.theme,
      skin: ajustes.skin,
      transparency: ajustes.transparency,
      journalPrivateMode: ajustes.journalPrivateMode,
    },
    ...(abortar ? { signal: abortar.signal } : {}),
  });
  // Solo se limpia si nadie cambió los ajustes mientras subían: si no, ese
  // segundo cambio no se mandaba nunca.
  await db.transaction('rw', db.meta, async () => {
    const ahora = await db.meta.get('ajustesSucios');
    if (ahora?.value === sucio.value) await db.meta.delete('ajustesSucios');
  });
}

async function aplicarAjustesDelServidor(ajustes: Record<string, unknown>): Promise<void> {
  const actual = await db.settings.get('unica');
  if (!actual) return;
  /**
   * Los ajustes del servidor se aplican **sin encolar nada**: vienen de él, así
   * que reenviárselos sería un bucle. Y `dayStartOffsetMin` no reescribe ninguna
   * entrada histórica: las fechas locales ya escritas son hechos (escenario S11).
   */
  await db.settings.put({ ...actual, ...ajustes, id: 'unica' });
}

/**
 * Cuando llega un HLC remoto, el reloj local aprende de él. Es lo que hace que dos
 * dispositivos que nunca se han visto acaben con relojes comparables, y está aquí
 * —y no en el `pull`— porque el único HLC que el cliente ve es el de la respuesta
 * del push.
 */
export async function aprenderDelReloj(hlcRemoto: string): Promise<void> {
  try {
    const remoto = parseHlc(hlcRemoto);
    await guardarHlcRemoto((local) => serializeHlc(receiveHlc(local, remoto, Date.now())));
  } catch {
    // Un HLC mal formado del servidor no debe romper la sincronización.
  }
}
